import {t} from "@lingui/core/macro";
import {StyleSheet} from "aphrodite";
import React, {useState} from "react";

import {View} from "@khanacademy/wonder-blocks-core";
import {color, spacing} from "@khanacademy/wonder-blocks-tokens";
import {
    Body,
    HeadingLarge,
    LabelSmall,
} from "@khanacademy/wonder-blocks-typography";
import {ConversionContextProvider, enums} from "@khan/analytics-learning-time";
import {mediaQueries} from "@khan/shared-styles";
import {useLocation} from "@khan/urls";

import TabbedPanel, {
    type Tab,
} from "#components/tabbed-panel-package/tabbed-panel.tsx";

import MyDocuments from "../resource-tab/my-documents.tsx";

import AITools from "./ai-tools.tsx";
import {publishAiGuideDocumentsToolsTabClickedEvent} from "./publish-ai-guide-documents-tools-tab-clicked-event.ts";
import {Platform} from "./utils.ts";

export enum Tabs {
    AI_TOOLS = "AI_TOOLS",
    DOCUMENTS = "DOCUMENTS",
}

enum Side {
    LEFT = "LEFT",
    RIGHT = "RIGHT",
}

export type TabId = Tabs.AI_TOOLS | Tabs.DOCUMENTS;

const getTabStyles = (isSelected: boolean, side: Side) => {
    const additionalStyles = isSelected
        ? []
        : side === Side.LEFT
          ? [styles.tabLeftDeselected]
          : [styles.tableRightDeselected];

    return [
        styles.tab,
        side === Side.LEFT ? styles.tabLeft : styles.tabRight,
        isSelected ? styles.selectedTab : styles.deselectedTab,
        ...additionalStyles,
    ];
};

type Props = {
    platform: Platform;
};

const AIToolsAndDocs = ({platform}: Props) => {
    const {queryParams} = useLocation();
    const [tab, setTab] = useState<Tabs>(
        queryParams.tab === Tabs.DOCUMENTS ? Tabs.DOCUMENTS : Tabs.AI_TOOLS,
    );

    const tabs: ReadonlyArray<Tab<TabId>> = [
        {
            key: Tabs.AI_TOOLS,
            renderTab: (isTabSelected: boolean) => ({
                isTabDisabled: false,
                tabHeader: (
                    <View style={getTabStyles(isTabSelected, Side.LEFT)}>
                        <LabelSmall
                            style={
                                isTabSelected ? [styles.tabLabelSelected] : []
                            }
                        >
                            {t`Tools`}
                        </LabelSmall>
                    </View>
                ),
            }),
            renderContent: () => (
                <View style={styles.pagePadding}>
                    <AITools platform={platform} />
                </View>
            ),
        },
        {
            key: Tabs.DOCUMENTS,
            renderTab: (isTabSelected: boolean) => ({
                isTabDisabled: false,
                tabHeader: (
                    <View style={getTabStyles(isTabSelected, Side.RIGHT)}>
                        <LabelSmall
                            style={
                                isTabSelected ? [styles.tabLabelSelected] : []
                            }
                        >
                            {t`My Documents`}
                        </LabelSmall>
                    </View>
                ),
            }),
            renderContent: () => (
                <View style={styles.pagePadding}>
                    <MyDocuments platform={platform} setTab={setTab} />
                </View>
            ),
        },
    ];

    return (
        <ConversionContextProvider
            learningTimeInfo={{
                product:
                    platform === Platform.Canvas
                        ? enums.Product.INSTRUCTURE_CANVAS
                        : enums.Product.OTHER_PRODUCT,
                activity: enums.Activity.NAVIGATING,
            }}
        >
            <TabbedPanel
                id="ai-tools-and-docs"
                tabListAriaLabel={t`Khanmigo Tools`}
                tabs={tabs}
                selectedTabKey={tab}
                onTabChanged={(newTabId: TabId) => {
                    setTab(newTabId);
                    publishAiGuideDocumentsToolsTabClickedEvent({
                        tabClicked:
                            newTabId === Tabs.AI_TOOLS
                                ? "TOOLS"
                                : "MY_DOCUMENTS",
                    });
                }}
            >
                {(renderTabs, renderContentPanel) => (
                    <>
                        <View style={[styles.header, styles.pagePadding]}>
                            <View style={styles.text}>
                                <HeadingLarge>{t`Khanmigo Tools`}</HeadingLarge>
                                <Body style={styles.textBody}>
                                    {t`Free AI powered tools designed to save you time and improve instruction!`}
                                </Body>
                            </View>
                            <View>{renderTabs()}</View>
                        </View>
                        {renderContentPanel()}
                    </>
                )}
            </TabbedPanel>
        </ConversionContextProvider>
    );
};

const BORDER_RADIUS = spacing.xxSmall_6;

const styles = StyleSheet.create({
    tab: {
        borderWidth: "thin",
        borderColor: color.offBlack32,
        borderStyle: "solid",
        padding: spacing.xxxSmall_4,
        minWidth: 160,
        minHeight: 40,
        justifyContent: "center",
        alignItems: "center",
        flexDirection: "row",
        [mediaQueries.smOrSmaller]: {
            marginTop: spacing.small_12,
        },
        overflow: "none",
    },
    tabRight: {
        borderTopRightRadius: BORDER_RADIUS,
        borderBottomRightRadius: BORDER_RADIUS,
    },
    tabLeft: {
        borderTopLeftRadius: BORDER_RADIUS,
        borderBottomLeftRadius: BORDER_RADIUS,
    },
    selectedTab: {
        color: color.blue,
        borderColor: color.blue,
        boxShadow: `inset 0px -${spacing.xxxSmall_4}px ${color.blue}`,
    },
    deselectedTab: {
        ":hover": {
            boxShadow: `inset 0px -${spacing.xxxSmall_4}px ${color.offBlack8}`,
        },
    },
    tabLeftDeselected: {
        borderRight: "none",
    },
    tableRightDeselected: {
        borderLeft: "none",
    },
    tabLabelSelected: {
        fontWeight: "bold",
    },
    text: {
        [mediaQueries.smOrLarger]: {
            flexGrow: 2,
            // Basis is width because flex-direction is row.
            flexBasis: 200,
        },
        [mediaQueries.xsOrSmaller]: {
            margin: 0,
            // Basis is height because flex-direction is column.
            flexBasis: 28,
        },
    },
    textBody: {
        lineHeight: "22px",
        marginTop: spacing.xSmall_8,
        [mediaQueries.xsOrSmaller]: {
            display: "none",
            marginTop: 0,
        },
        marginRight: spacing.xSmall_8,
    },
    header: {
        flexDirection: "row",
        justifyContent: "space-between",
        paddingRight: spacing.small_12,
        marginBottom: spacing.large_24,
        [mediaQueries.xsOrSmaller]: {
            flexDirection: "column",
        },
    },
    pagePadding: {
        // This is needed so that the outlines that appear when hovering over
        // the category pills don't get cut off
        padding: spacing.xxxSmall_4,
    },
});

export default AIToolsAndDocs;
